import {connect} from 'react-redux';

import {makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {isTeammateNamespace} from '../../../../helpers/models/namespaceHelpers';
import {LensModel} from '../../../../models/api/lensModel';
import {VisualWorkspaceLens} from '../../../../models/api/visualWorkspaceModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
  WorkspaceDisplayChevronActionsEnum,
  WorkspaceDisplayChevronSectionsEnum,
} from '../../../../services/tracking';
import {AppState} from '../../../../store/appState';
import {toggleExpandItemAction} from '../../../../store/data/localPreferences/localPreferencesActions';
import {
  getShouldAlphabetizeWorkspace,
  getShouldUseOpenFilterForTeams,
} from '../../../../store/data/preferences/preferencesSelectors';
import {makeGetWorkspacesSelectedRefinements} from '../../../../store/data/refinements/refinementsSelectors';
import {
  getIsUserGlobalAdmin,
  getUserId,
  getUserLicenseType,
  getUserNamespace,
} from '../../../../store/data/teammates/teammatesSelectors';
import {makeGetVisualWorkspaceLenses} from '../../../../store/data/workspace/visualWorkspace/visualWorkspaceSelectors';
import {
  hideLensesWorkspaceAsyncAction,
  showWorkspaceLensAsyncAction,
} from '../../../../store/data/workspace/workspaceActions';
import {
  defaultIsLensesWorkspaceExpanded,
  lensesWorkspaceExpandedKey,
  makeGetIsLensesWorkspaceExpanded,
} from '../../../../store/data/workspace/workspaceExpandedSelectors';
import {
  dismissCreateLensDialog,
  showCreateLensDialog,
} from '../../../../store/panels/inboxes/inboxesPanelActions';
import {makeGetIsCreatingLens} from '../../../../store/panels/inboxes/inboxesPanelSelectors';
import {
  buildTeamLensRefinementKey,
  buildTeammateLensRefinementKey,
} from '../../../../store/panels/inboxes/parameters/inboxesParametersSelectors';
import {InboxesPanelId} from '../../../../store/panels/panelStateBase';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {LensesWorkspace} from './lensesWorkspace';

/*
 * Props.
 */

interface LensesWorkspaceContainerProps {
  visualWorkspaceLenses: ReadonlyArray<VisualWorkspaceLens>;
  panelId: InboxesPanelId;
}

/*
 * Selectors.
 */

function getVisualWorkspaceLenses(_state: AppState, props: LensesWorkspaceContainerProps) {
  return props.visualWorkspaceLenses;
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<LensesWorkspaceContainerProps>()(() => {
  const getLenses = makeGetVisualWorkspaceLenses(getVisualWorkspaceLenses);
  const getIsCreateDialogOpen = makeGetIsCreatingLens();
  const getLensesSelectedRefinements = makeGetWorkspacesSelectedRefinements<
    LensesWorkspaceContainerProps,
    LensModel
  >(getLenses, (lens) =>
    isTeammateNamespace(lens.namespace)
      ? buildTeammateLensRefinementKey(lens.id)
      : buildTeamLensRefinementKey(lens.id),
  );
  return {
    lenses: getLenses,
    lensesSelectedRefinements: getLensesSelectedRefinements,
    userId: getUserId,
    userNamespace: getUserNamespace,
    userLicenseType: getUserLicenseType,
    isUserGlobalAdmin: getIsUserGlobalAdmin,
    isExpanded: makeGetIsLensesWorkspaceExpanded(),
    shouldAlphabetizeWorkspace: getShouldAlphabetizeWorkspace,
    shouldUseOpenFilterForTeams: getShouldUseOpenFilterForTeams,
    isCreateDialogOpen: getIsCreateDialogOpen,
  };
});

export const LensesWorkspaceContainer = connect(
  mapStateToProps,
  {
    toggleExpandItemAction,
    navigateToLinkAction,
    hideLensesWorkspaceAsyncAction,
    showWorkspaceLensAsyncAction,
    showCreateLensDialog,
    dismissCreateLensDialog,
  },
  (stateProps, dispatchProps, ownProps) => {
    const onToggleExpandClick = () => {
      trackInterfaceEvent(InterfaceEventsEnum.P1_WORKSPACE_CHEVRON_CLICK, {
        location: WorkspaceDisplayChevronSectionsEnum.LENSES,
        action: stateProps.isExpanded
          ? WorkspaceDisplayChevronActionsEnum.COLLAPSE
          : WorkspaceDisplayChevronActionsEnum.EXPAND,
      });
      dispatchProps.toggleExpandItemAction(lensesWorkspaceExpandedKey, defaultIsLensesWorkspaceExpanded);
    };

    const onHideSection = () => {
      dispatchProps.hideLensesWorkspaceAsyncAction();
    };

    const showCreate = () => dispatchProps.showCreateLensDialog(ownProps.panelId);
    const hideCreate = () => dispatchProps.dismissCreateLensDialog(ownProps.panelId);

    // Merge the props.
    return {
      ...stateProps,
      ...ownProps,
      onToggleExpandClick,
      onHideSection,
      onCreateLens: dispatchProps.showWorkspaceLensAsyncAction,
      showCreateLensDialog: showCreate,
      hideCreateLensDialog: hideCreate,
    };
  },
)(LensesWorkspace);
